import type { TranslationModule } from '../types';

export const financeTranslations: TranslationModule = {
  ro: {
    pageTitle: "Piețe Financiare — Curs BNR, Indicatori Macro & Burse",
    pageSubtitle: "Informații financiare oficiale, curs valutar BNR actualizat, convertor valutar, indicatori macroeconomici BCE/Eurostat și catalogul bursier oficial.",
    financialMarkets: "Piețe Financiare",
    navOverview: "Panou General",
    navFx: "Curs BNR & Convertor",
    navMacro: "Indicatori Macro",
    navBvb: "Burse & Acțiuni",
    navNews: "Știri Economice",

    // Overview cards
    latestBnrDate: "Data de referință BNR",
    officialBnrRates: "Curs Oficial BNR",
    officialBnrRatesDesc: "Cursul de referință publicat în fiecare zi bancară în jurul orei 13:00.",
    converterTitle: "Convertor Valutar Oficial",
    converterDesc: "Conversie exactă pe baza cursurilor de schimb oficiale comunicate de BNR.",
    macroTitle: "Indicatori Economici Cheie",
    macroDesc: "Date oficiale agregate de la Eurostat, World Bank și Banca Centrală Europeană.",
    bvbTitle: "Bursa de Valori București",
    bvbDesc: "Indici principali (BET, BET-TR) și cele mai tranzacționate societăți listate.",
    newsTitle: "Comunicate & Știri Instituționale",
    newsDesc: "Flux oficial de comunicate de la BCE și Eurostat.",

    // Converter
    amount: "Sumă",
    fromCurrency: "Din moneda",
    toCurrency: "În moneda",
    convertButton: "Convertește",
    swapCurrencies: "Inversează monedele",
    quickAmounts: "Sume rapide",
    conversionFormula: "Formula de calcul",
    officialRateUsed: "Curs utilizat",
    converterDisclaimer: "Calculul are la bază cursul oficial de referință al Băncii Naționale a României. Valorile sunt orientative și legale/fiscale, neconstituind cotație bancară comercială de tranzacționare.",

    // FX Table
    searchPlaceholder: "Caută după monedă sau denumire (ex: EUR, Dolar, Aur)...",
    allCurrencies: "Toate valutele",
    majorCurrencies: "Principale (EUR, USD, GBP, CHF, XAU)",
    tableCode: "Valută",
    tableName: "Denumire oficială",
    tableRate: "Curs (RON)",
    tableChange: "Variație",
    tableMultiplier: "Multiplicator",
    tableAction: "Grafic",
    goldNote: "Gram de aur pur (XAU)",
    multiplierNote: "Cotație per {mult} unități",
    noRatesFound: "Nicio valută găsită conform criteriilor.",
    ratesDisclaimer: "Cursul valutar este cel oficial comunicat periodic de Banca Națională a României. Nu reprezintă cotație continuă de tranzacționare în timp real.",

    // History chart
    chartTitle: "Evoluție Curs {symbol}/RON",
    chartSubtitle: "Istoricul ultimelor 30 de cotații oficiale",
    minRate: "Minim",
    maxRate: "Maxim",
    avgRate: "Medie",
    closeChart: "Închide graficul",
    toggleDataTable: "Arată tabelul de date",

    // Macro
    inflationRo: "Inflație România (IAPC)",
    inflationEa: "Inflație Zona Euro (IAPC)",
    ecbInterestRate: "Dobânda facilității de depozit BCE",
    gdpRo: "Produsul Intern Brut (România)",
    source: "Sursă oficială",
    referencePeriod: "Perioadă de referință",

    // BVB & Markets
    bvbNotice: "Informații cu caracter educativ și de orientare. Cotațiile bursiere în timp real sunt disponibile exclusiv la operatorul de piață: bvb.ro.",
    bvbIndicesTitle: "Indici Bursa de Valori București (BVB)",
    bvbCompaniesTitle: "Top Companii Listate la BVB",
    internationalIndicesTitle: "Indici Bursieri Internaționali de Referință",
    visitOfficialSite: "Vezi profilul oficial pe bvb.ro",
    ticker: "Simbol",
    sector: "Sector",
    country: "Țară / Piață",

    // News
    readOfficialArticle: "Citește comunicatul oficial",
    noNewsAvailable: "Momentan nu sunt comunicate recente disponibile.",
    filterAll: "Toate",
    filterEcb: "Banca Centrală Europeană",
    filterEurostat: "Eurostat",
  },
  en: {
    pageTitle: "Financial Markets — NBR FX Rates, Macro Indicators & Stock Exchanges",
    pageSubtitle: "Official financial information, updated NBR exchange rates, currency converter, ECB/Eurostat macroeconomic indicators, and official stock market catalog.",
    financialMarkets: "Financial Markets",
    navOverview: "Overview",
    navFx: "NBR Rates & Converter",
    navMacro: "Macro Indicators",
    navBvb: "Stock Markets",
    navNews: "Economic News",

    // Overview cards
    latestBnrDate: "NBR Reference Date",
    officialBnrRates: "Official NBR FX Rates",
    officialBnrRatesDesc: "Reference rates published every banking business day around 13:00 Bucharest time.",
    converterTitle: "Official Currency Converter",
    converterDesc: "Exact conversion using official reference exchange rates issued by the National Bank of Romania.",
    macroTitle: "Key Macroeconomic Indicators",
    macroDesc: "Official aggregated data from Eurostat, World Bank, and the European Central Bank.",
    bvbTitle: "Bucharest Stock Exchange (BVB)",
    bvbDesc: "Core indices (BET, BET-TR) and largest liquid listed companies.",
    newsTitle: "Institutional Press Releases",
    newsDesc: "Official feeds directly from the ECB and Eurostat.",

    // Converter
    amount: "Amount",
    fromCurrency: "From currency",
    toCurrency: "To currency",
    convertButton: "Convert",
    swapCurrencies: "Swap currencies",
    quickAmounts: "Quick amounts",
    conversionFormula: "Conversion formula",
    officialRateUsed: "Applied rate",
    converterDisclaimer: "Calculations are based on the official reference exchange rate published by the National Bank of Romania. For reference and fiscal purposes only.",

    // FX Table
    searchPlaceholder: "Search by code or name (e.g., EUR, Dollar, Gold)...",
    allCurrencies: "All currencies",
    majorCurrencies: "Major (EUR, USD, GBP, CHF, XAU)",
    tableCode: "Currency",
    tableName: "Official Name",
    tableRate: "Rate (RON)",
    tableChange: "Change",
    tableMultiplier: "Multiplier",
    tableAction: "Chart",
    goldNote: "Gram of pure gold (XAU)",
    multiplierNote: "Quoted per {mult} units",
    noRatesFound: "No currencies matching your search.",
    ratesDisclaimer: "Exchange rates are official periodic references published by the National Bank of Romania, not real-time banking execution quotes.",

    // History chart
    chartTitle: "Rate History {symbol}/RON",
    chartSubtitle: "Last 30 official banking rates",
    minRate: "Minimum",
    maxRate: "Maximum",
    avgRate: "Average",
    closeChart: "Close chart",
    toggleDataTable: "Show data table",

    // Macro
    inflationRo: "Romania Inflation (HICP)",
    inflationEa: "Euro Area Inflation (HICP)",
    ecbInterestRate: "ECB Deposit Facility Rate",
    gdpRo: "Gross Domestic Product (Romania)",
    source: "Official source",
    referencePeriod: "Reference period",

    // BVB & Markets
    bvbNotice: "Educational and reference directory. Live trading prices and books are exclusively provided by the market operator at bvb.ro.",
    bvbIndicesTitle: "Bucharest Stock Exchange Indices",
    bvbCompaniesTitle: "Top Listed Companies on BVB",
    internationalIndicesTitle: "Major Global Stock Indices",
    visitOfficialSite: "View official profile on bvb.ro",
    ticker: "Symbol",
    sector: "Sector",
    country: "Country / Market",

    // News
    readOfficialArticle: "Read official release",
    noNewsAvailable: "No recent official releases found.",
    filterAll: "All",
    filterEcb: "European Central Bank",
    filterEurostat: "Eurostat",
  },
  fr: {
    pageTitle: "Marchés Financiers — Taux BNR, Indicateurs Macro & Bourses",
    pageSubtitle: "Informations financières officielles, taux de change BNR, convertisseur, indicateurs macroéconomiques BCE/Eurostat et catalogue boursier officiel.",
    financialMarkets: "Marchés Financiers",
    navOverview: "Aperçu",
    navFx: "Taux BNR & Convertisseur",
    navMacro: "Indicateurs Macro",
    navBvb: "Bourses & Actions",
    navNews: "Actualités Économiques",

    // Overview cards
    latestBnrDate: "Date de référence BNR",
    officialBnrRates: "Taux de Change Officiels BNR",
    officialBnrRatesDesc: "Taux de référence publiés chaque jour ouvrable vers 13h00 heure de Bucarest.",
    converterTitle: "Convertisseur de Devises Officiel",
    converterDesc: "Conversion exacte calculée d'après les cours de référence de la Banque Nationale de Roumanie.",
    macroTitle: "Principaux Indicateurs Macroéconomiques",
    macroDesc: "Données officielles d'Eurostat, de la Banque Mondiale et de la Banque Centrale Européenne.",
    bvbTitle: "Bourse des Valeurs de Bucarest (BVB)",
    bvbDesc: "Principaux indices (BET, BET-TR) et plus grandes sociétés cotées.",
    newsTitle: "Communiqués & Actualités Institutionnelles",
    newsDesc: "Flux officiel de communiqués de la BCE et d'Eurostat.",

    // Converter
    amount: "Montant",
    fromCurrency: "Devise d'origine",
    toCurrency: "Devise de destination",
    convertButton: "Convertir",
    swapCurrencies: "Inverser les devises",
    quickAmounts: "Montants rapides",
    conversionFormula: "Formule de calcul",
    officialRateUsed: "Taux appliqué",
    converterDisclaimer: "Calcul basé sur le cours officiel de référence publié par la BNR. À titre indicatif et légal/fiscal.",

    // FX Table
    searchPlaceholder: "Rechercher par code ou nom (ex: EUR, Dollar, Or)...",
    allCurrencies: "Toutes les devises",
    majorCurrencies: "Principales (EUR, USD, GBP, CHF, XAU)",
    tableCode: "Devise",
    tableName: "Désignation officielle",
    tableRate: "Taux (RON)",
    tableChange: "Variation",
    tableMultiplier: "Multiplicateur",
    tableAction: "Graphique",
    goldNote: "Gramme d'or pur (XAU)",
    multiplierNote: "Cotation pour {mult} unités",
    noRatesFound: "Aucune devise ne correspond à votre recherche.",
    ratesDisclaimer: "Les taux de change sont des références officielles communiquées par la BNR, et non des cotations commerciales en temps réel.",

    // History chart
    chartTitle: "Évolution du Taux {symbol}/RON",
    chartSubtitle: "Historique des 30 dernières cotations officielles",
    minRate: "Minimum",
    maxRate: "Maximum",
    avgRate: "Moyenne",
    closeChart: "Fermer le graphique",
    toggleDataTable: "Afficher le tableau des données",

    // Macro
    inflationRo: "Inflation Roumanie (IPCH)",
    inflationEa: "Inflation Zone Euro (IPCH)",
    ecbInterestRate: "Taux de la facilité de dépôt BCE",
    gdpRo: "Produit Intérieur Brut (Roumanie)",
    source: "Source officielle",
    referencePeriod: "Période de référence",

    // BVB & Markets
    bvbNotice: "Répertoire informatif et éducatif. Les cotations boursières en temps réel sont disponibles sur le site officiel bvb.ro.",
    bvbIndicesTitle: "Indices de la Bourse de Bucarest",
    bvbCompaniesTitle: "Principales Sociétés Cotées (BVB)",
    internationalIndicesTitle: "Grands Indices Boursiers Internationaux",
    visitOfficialSite: "Voir la fiche officielle sur bvb.ro",
    ticker: "Symbole",
    sector: "Secteur",
    country: "Pays / Marché",

    // News
    readOfficialArticle: "Lire le communiqué officiel",
    noNewsAvailable: "Aucun communiqué récent disponible.",
    filterAll: "Tous",
    filterEcb: "Banque Centrale Européenne",
    filterEurostat: "Eurostat",
  }
};
